import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, ChevronRight, Check, Sparkles, Trophy, Loader2, RefreshCw } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import AudioButton from '../components/common/AudioButton';
import { useApp } from '../context/AppContext';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LessonPage = () => {
  const { trackId, lessonId } = useParams();
  const navigate = useNavigate();
  const { completeLesson } = useApp();

  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [quizIndex, setQuizIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [lessonComplete, setLessonComplete] = useState(false);
  
  // Exercise states
  const [exercisePhase, setExercisePhase] = useState(false);
  const [exerciseIndex, setExerciseIndex] = useState(0);
  const [exerciseAnswer, setExerciseAnswer] = useState('');
  const [exerciseChecked, setExerciseChecked] = useState(false);
  const [exerciseCorrect, setExerciseCorrect] = useState(false);
  const [wordOrderAnswer, setWordOrderAnswer] = useState([]);
  const [availableWords, setAvailableWords] = useState([]);

  // Fetch lesson content from MongoDB API
  useEffect(() => {
    const fetchLesson = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/lessons/${lessonId}`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setLesson({
            ...data,
            title: data.title || data.titleNative || 'Lição',
            titlePt: data.titleNative || data.title || 'Lição',
            xp: data.xp || 50,
            sections: data.sections || [],
            quiz: data.quiz || [],
            exercises: data.exercises || []
          });
        } else {
          setLesson({
            id: lessonId,
            title: 'Lição',
            titlePt: 'Lição',
            xp: 50,
            sections: [{ type: 'intro', title: 'Erro', content: 'Não foi possível carregar a lição.' }],
            quiz: [{ id: 'q1', type: 'multiple-choice', question: 'Continue praticando!', options: ['OK', 'Certo', 'Vamos!', 'Sim'], correct: 0 }],
            exercises: []
          });
        }
      } catch (error) {
        console.error('Error fetching lesson:', error);
        setLesson({
          id: lessonId,
          title: 'Lição',
          titlePt: 'Lição',
          xp: 50,
          sections: [{ type: 'intro', title: 'Erro', content: 'Erro ao carregar a lição.' }],
          quiz: [{ id: 'q1', type: 'multiple-choice', question: 'Tente novamente!', options: ['OK'], correct: 0 }],
          exercises: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLesson();
  }, [lessonId]);

  // Loading state
  if (loading || !lesson) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Carregando lição...</p>
        </div>
      </div>
    );
  }

  const hasExercises = lesson.exercises && lesson.exercises.length > 0;
  const totalSteps = lesson.sections.length + (hasExercises ? lesson.exercises.length : 0) + lesson.quiz.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Determine current content type
  const sectionsEnd = lesson.sections.length;
  const exercisesEnd = sectionsEnd + (hasExercises ? lesson.exercises.length : 0);
  
  const currentSection = currentStep < sectionsEnd ? lesson.sections[currentStep] : null;
  const currentExercise = hasExercises && currentStep >= sectionsEnd && currentStep < exercisesEnd 
    ? lesson.exercises[currentStep - sectionsEnd] 
    : null;
  const currentQuiz = currentStep >= exercisesEnd ? lesson.quiz[quizIndex] : null;

  const handleNext = () => {
    if (currentStep < sectionsEnd - 1) {
      // More sections
      setCurrentStep(prev => prev + 1);
    } else if (currentStep === sectionsEnd - 1) {
      // Finished sections, start exercises or quiz
      setCurrentStep(prev => prev + 1);
      if (hasExercises) {
        setExercisePhase(true);
        initExercise(lesson.exercises[0]);
      }
    }
  };

  const initExercise = (exercise) => {
    setExerciseAnswer('');
    setExerciseChecked(false);
    setExerciseCorrect(false);
    setWordOrderAnswer([]);
    if (exercise?.type === 'word-order' && exercise.words) {
      setAvailableWords([...exercise.words].sort(() => Math.random() - 0.5));
    }
  };

  const handleExerciseCheck = () => {
    if (!currentExercise) return;
    
    let isCorrect = false;
    
    if (currentExercise.type === 'fill-blank') {
      isCorrect = exerciseAnswer.toLowerCase().trim() === currentExercise.answer.toLowerCase().trim();
    } else if (currentExercise.type === 'word-order') {
      isCorrect = JSON.stringify(wordOrderAnswer) === JSON.stringify(currentExercise.correct);
    } else if (currentExercise.type === 'translation') {
      const userAnswer = exerciseAnswer.toLowerCase().trim();
      const correctAnswer = currentExercise.answer.toLowerCase().trim();
      isCorrect = userAnswer === correctAnswer || 
                  correctAnswer.includes(userAnswer) || 
                  userAnswer.includes(correctAnswer);
    }
    
    setExerciseChecked(true);
    setExerciseCorrect(isCorrect);
    if (isCorrect) {
      setCorrectAnswers(prev => prev + 1);
    }
  };

  const handleExerciseNext = () => {
    const nextExIndex = currentStep - sectionsEnd + 1;
    if (nextExIndex < lesson.exercises.length) {
      setCurrentStep(prev => prev + 1);
      initExercise(lesson.exercises[nextExIndex]);
    } else {
      // Move to quiz
      setExercisePhase(false);
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleWordClick = (word, fromAnswer = false) => {
    if (exerciseChecked) return;
    
    if (fromAnswer) {
      setWordOrderAnswer(prev => prev.filter(w => w !== word));
      setAvailableWords(prev => [...prev, word]);
    } else {
      setAvailableWords(prev => prev.filter(w => w !== word));
      setWordOrderAnswer(prev => [...prev, word]);
    }
  };

  const handleAnswerSelect = (index) => {
    if (isAnswered) return;
    setSelectedAnswer(index);
  };

  const handleCheckAnswer = () => {
    if (selectedAnswer === null) return;
    setIsAnswered(true);
    
    if (selectedAnswer === currentQuiz.correct) {
      setCorrectAnswers(prev => prev + 1);
    }
  };

  const handleNextQuestion = () => {
    if (quizIndex < lesson.quiz.length - 1) {
      setQuizIndex(prev => prev + 1);
      setCurrentStep(prev => prev + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    } else {
      setLessonComplete(true);
      completeLesson(trackId, lessonId, lesson.xp);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleFinish = () => {
    navigate(`/track/${trackId}`);
  };

  // Lesson Complete Screen
  if (lessonComplete) {
    const totalQuestions = lesson.quiz.length + (hasExercises ? lesson.exercises.length : 0);
    
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center space-y-6 animate-scale-in">
            <div className="w-24 h-24 rounded-full bg-success/10 flex items-center justify-center mx-auto celebrate">
              <Trophy className="w-12 h-12 text-success" />
            </div>
            
            <div className="space-y-2">
              <h1 className="font-display text-2xl font-bold">Parabéns! 🎉</h1>
              <p className="text-muted-foreground">Você completou a lição</p>
            </div>

            <div className="card-elevated p-6 space-y-4 max-w-xs mx-auto">
              <h2 className="font-semibold">{lesson.title}</h2>
              
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-3 rounded-xl bg-primary/10">
                  <p className="text-2xl font-bold text-primary">+{lesson.xp}</p>
                  <p className="text-xs text-muted-foreground">XP Ganhos</p>
                </div>
                <div className="p-3 rounded-xl bg-success/10">
                  <p className="text-2xl font-bold text-success">{correctAnswers}/{totalQuestions}</p>
                  <p className="text-xs text-muted-foreground">Respostas Certas</p>
                </div>
              </div>
            </div>

            <Button 
              onClick={handleFinish}
              className="btn-primary-gradient px-8"
              data-testid="lesson-complete-continue-btn"
            >
              Continuar
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background border-b border-border px-4 py-3">
        <div className="flex items-center gap-4 max-w-lg mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="shrink-0"
            data-testid="lesson-close-btn"
          >
            <X className="w-5 h-5" />
          </Button>
          
          <div className="flex-1">
            <Progress value={progress} className="h-2" />
          </div>
          
          <span className="text-sm text-muted-foreground shrink-0">
            {currentStep + 1}/{totalSteps}
          </span>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-lg mx-auto w-full p-4">
        {/* Section Content */}
        {currentSection && (
          <div className="space-y-6 animate-fade-in">
            {/* Intro Section */}
            {currentSection.type === 'intro' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-primary">
                  <Sparkles className="w-5 h-5" />
                  <h2 className="font-semibold">{currentSection.title}</h2>
                </div>
                <div className="card-elevated p-5">
                  <p className="text-muted-foreground leading-relaxed">
                    {currentSection.content}
                  </p>
                </div>
              </div>
            )}

            {/* Vocabulary Section */}
            {currentSection.type === 'vocabulary' && (
              <div className="space-y-4">
                <h2 className="font-display font-semibold text-lg">{currentSection.title}</h2>
                <div className="space-y-3">
                  {currentSection.words?.map((word, index) => (
                    <div 
                      key={index}
                      className="card-lesson flex items-center justify-between"
                      data-testid={`vocab-word-${index}`}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-lg">{word.target || word.english || word.en}</p>
                        {word.reading && (
                          <p className="text-sm text-primary">{word.reading}</p>
                        )}
                        {word.phonetic && (
                          <p className="text-xs text-muted-foreground">{word.phonetic}</p>
                        )}
                        <p className="text-sm text-muted-foreground">{word.pt}</p>
                      </div>
                      <AudioButton text={word.target || word.english || word.en} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tip Section */}
            {currentSection.type === 'tip' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-warning">
                  <Sparkles className="w-5 h-5" />
                  <h2 className="font-semibold">{currentSection.title}</h2>
                </div>
                <div className="card-elevated p-5 border-l-4 border-warning">
                  <p className="text-muted-foreground leading-relaxed">
                    {currentSection.content}
                  </p>
                </div>
              </div>
            )}

            {/* Dialog Section */}
            {currentSection.type === 'dialog' && (
              <div className="space-y-4">
                <h2 className="font-display font-semibold text-lg">{currentSection.title}</h2>
                <div className="space-y-3">
                  {currentSection.conversation?.map((line, index) => (
                    <div 
                      key={index}
                      className={cn(
                        'p-4 rounded-xl',
                        line.speaker === 'You' 
                          ? 'bg-primary/10 ml-8' 
                          : 'bg-muted mr-8'
                      )}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          {line.speaker === 'You' ? 'Você' : line.speaker}
                        </span>
                        <AudioButton text={line.text} size="sm" />
                      </div>
                      <p className="font-medium">{line.text}</p>
                      <p className="text-sm text-muted-foreground mt-1">{line.translation}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Practice Section */}
            {currentSection.type === 'practice' && (
              <div className="space-y-4">
                <h2 className="font-display font-semibold text-lg">{currentSection.title}</h2>
                {currentSection.instruction && (
                  <p className="text-muted-foreground">{currentSection.instruction}</p>
                )}
                <div className="space-y-3">
                  {currentSection.phrases?.map((phrase, index) => (
                    <div 
                      key={index}
                      className="card-elevated p-4 flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-lg">{phrase.target || phrase.english || phrase.en}</p>
                        <p className="text-sm text-muted-foreground">{phrase.pt}</p>
                      </div>
                      <AudioButton text={phrase.target || phrase.english || phrase.en} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cultural Tip Section */}
            {currentSection.type === 'cultural' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-accent">
                  <span className="text-2xl">🌍</span>
                  <h2 className="font-semibold">{currentSection.title}</h2>
                </div>
                <div className="card-elevated p-5 border-l-4 border-accent">
                  <p className="text-muted-foreground leading-relaxed">
                    {currentSection.content}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Exercise Content */}
        {currentExercise && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 text-xs font-medium bg-accent/10 text-accent rounded-full">
                Exercício {currentStep - sectionsEnd + 1}/{lesson.exercises.length}
              </span>
            </div>

            {/* Fill in the Blank */}
            {currentExercise.type === 'fill-blank' && (
              <div className="space-y-4">
                <h2 className="font-display text-xl font-semibold">
                  Complete a frase:
                </h2>
                <div className="card-elevated p-4">
                  <p className="text-lg font-medium">{currentExercise.sentence}</p>
                  {currentExercise.hint && (
                    <p className="text-sm text-muted-foreground mt-2">💡 {currentExercise.hint}</p>
                  )}
                </div>
                <Input
                  value={exerciseAnswer}
                  onChange={(e) => setExerciseAnswer(e.target.value)}
                  placeholder="Digite a resposta..."
                  disabled={exerciseChecked}
                  className="text-lg"
                  data-testid="fill-blank-input"
                />
                {exerciseChecked && (
                  <div className={cn(
                    'p-4 rounded-xl',
                    exerciseCorrect 
                      ? 'bg-success/10 border border-success/30' 
                      : 'bg-destructive/10 border border-destructive/30'
                  )}>
                    <p className="font-semibold">
                      {exerciseCorrect ? '✓ Correto!' : '✗ Incorreto'}
                    </p>
                    {!exerciseCorrect && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Resposta correta: <span className="font-medium">{currentExercise.answer}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Word Order */}
            {currentExercise.type === 'word-order' && (
              <div className="space-y-4">
                <h2 className="font-display text-xl font-semibold">
                  Organize as palavras:
                </h2>
                {currentExercise.translation && (
                  <p className="text-muted-foreground">Traduza: "{currentExercise.translation}"</p>
                )}
                
                {/* Answer area */}
                <div className="min-h-[60px] p-3 rounded-xl bg-primary/5 border-2 border-dashed border-primary/30 flex flex-wrap gap-2">
                  {wordOrderAnswer.map((word, idx) => (
                    <button
                      key={`answer-${idx}`}
                      onClick={() => handleWordClick(word, true)}
                      disabled={exerciseChecked}
                      className="px-3 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors"
                    >
                      {word}
                    </button>
                  ))}
                  {wordOrderAnswer.length === 0 && (
                    <span className="text-muted-foreground text-sm">Toque nas palavras abaixo...</span>
                  )}
                </div>

                {/* Available words */}
                <div className="flex flex-wrap gap-2">
                  {availableWords.map((word, idx) => (
                    <button
                      key={`available-${idx}`}
                      onClick={() => handleWordClick(word, false)}
                      disabled={exerciseChecked}
                      className="px-3 py-2 bg-muted hover:bg-muted/80 rounded-lg font-medium text-sm transition-colors"
                    >
                      {word}
                    </button>
                  ))}
                </div>

                {exerciseChecked && (
                  <div className={cn(
                    'p-4 rounded-xl',
                    exerciseCorrect 
                      ? 'bg-success/10 border border-success/30' 
                      : 'bg-destructive/10 border border-destructive/30'
                  )}>
                    <p className="font-semibold">
                      {exerciseCorrect ? '✓ Correto!' : '✗ Incorreto'}
                    </p>
                    {!exerciseCorrect && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Ordem correta: <span className="font-medium">{currentExercise.correct?.join(' ')}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Translation */}
            {currentExercise.type === 'translation' && (
              <div className="space-y-4">
                <h2 className="font-display text-xl font-semibold">
                  Traduza para inglês:
                </h2>
                <div className="card-elevated p-4">
                  <p className="text-lg font-medium">{currentExercise.source}</p>
                </div>
                <Input
                  value={exerciseAnswer}
                  onChange={(e) => setExerciseAnswer(e.target.value)}
                  placeholder="Digite a tradução..."
                  disabled={exerciseChecked}
                  className="text-lg"
                  data-testid="translation-input"
                />
                {exerciseChecked && (
                  <div className={cn(
                    'p-4 rounded-xl',
                    exerciseCorrect 
                      ? 'bg-success/10 border border-success/30' 
                      : 'bg-destructive/10 border border-destructive/30'
                  )}>
                    <p className="font-semibold">
                      {exerciseCorrect ? '✓ Correto!' : '✗ Incorreto'}
                    </p>
                    {!exerciseCorrect && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Resposta esperada: <span className="font-medium">{currentExercise.answer}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Quiz Content */}
        {currentQuiz && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-2 text-accent">
              <span className="px-2 py-1 text-xs font-medium bg-accent/10 rounded-full">
                Quiz {quizIndex + 1}/{lesson.quiz.length}
              </span>
            </div>

            <h2 className="font-display text-xl font-semibold">
              {currentQuiz.question}
            </h2>

            {currentQuiz.type === 'multiple-choice' && (
              <div className="space-y-3">
                {currentQuiz.options?.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    disabled={isAnswered}
                    data-testid={`quiz-option-${index}`}
                    className={cn(
                      'quiz-option w-full text-left',
                      selectedAnswer === index && !isAnswered && 'selected',
                      isAnswered && index === currentQuiz.correct && 'correct',
                      isAnswered && selectedAnswer === index && index !== currentQuiz.correct && 'incorrect'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        'w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0',
                        selectedAnswer === index && !isAnswered && 'border-primary bg-primary/10',
                        isAnswered && index === currentQuiz.correct && 'border-success bg-success/10',
                        isAnswered && selectedAnswer === index && index !== currentQuiz.correct && 'border-destructive bg-destructive/10',
                        selectedAnswer !== index && !isAnswered && 'border-border'
                      )}>
                        {isAnswered && index === currentQuiz.correct && (
                          <Check className="w-4 h-4 text-success" />
                        )}
                        {isAnswered && selectedAnswer === index && index !== currentQuiz.correct && (
                          <X className="w-4 h-4 text-destructive" />
                        )}
                        {!isAnswered && (
                          <span className="text-sm text-muted-foreground">
                            {String.fromCharCode(65 + index)}
                          </span>
                        )}
                      </div>
                      <span className="font-medium">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {isAnswered && (
              <div className={cn(
                'p-4 rounded-xl animate-slide-up',
                selectedAnswer === currentQuiz.correct 
                  ? 'bg-success/10 border border-success/30' 
                  : 'bg-destructive/10 border border-destructive/30'
              )}>
                <p className="font-semibold">
                  {selectedAnswer === currentQuiz.correct ? '✓ Correto!' : '✗ Incorreto'}
                </p>
                {selectedAnswer !== currentQuiz.correct && (
                  <p className="text-sm text-muted-foreground mt-1">
                    A resposta correta é: {currentQuiz.options?.[currentQuiz.correct]}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="sticky bottom-0 bg-background border-t border-border p-4">
        <div className="max-w-lg mx-auto">
          {currentSection && (
            <Button 
              onClick={handleNext}
              className="w-full btn-primary-gradient"
              data-testid="lesson-continue-btn"
            >
              {currentStep === sectionsEnd - 1 
                ? (hasExercises ? 'Começar Exercícios' : 'Começar Quiz')
                : 'Continuar'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {currentExercise && !exerciseChecked && (
            <Button 
              onClick={handleExerciseCheck}
              disabled={
                (currentExercise.type === 'fill-blank' && !exerciseAnswer.trim()) ||
                (currentExercise.type === 'word-order' && wordOrderAnswer.length === 0) ||
                (currentExercise.type === 'translation' && !exerciseAnswer.trim())
              }
              className="w-full btn-primary-gradient"
              data-testid="exercise-check-btn"
            >
              Verificar
            </Button>
          )}

          {currentExercise && exerciseChecked && (
            <Button 
              onClick={handleExerciseNext}
              className={cn(
                'w-full',
                exerciseCorrect ? 'btn-success-gradient' : 'btn-primary-gradient'
              )}
              data-testid="exercise-next-btn"
            >
              {currentStep - sectionsEnd + 1 === lesson.exercises.length ? 'Começar Quiz' : 'Próximo'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {currentQuiz && !isAnswered && (
            <Button 
              onClick={handleCheckAnswer}
              disabled={selectedAnswer === null}
              className="w-full btn-primary-gradient"
              data-testid="quiz-check-btn"
            >
              Verificar
            </Button>
          )}

          {currentQuiz && isAnswered && (
            <Button 
              onClick={handleNextQuestion}
              className={cn(
                'w-full',
                selectedAnswer === currentQuiz.correct ? 'btn-success-gradient' : 'btn-primary-gradient'
              )}
              data-testid="quiz-next-btn"
            >
              {quizIndex === lesson.quiz.length - 1 ? 'Finalizar' : 'Próxima'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
};

export default LessonPage;
